import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { addYears, formatDistanceToNow, isAfter } from 'date-fns';

import { DocusignData } from '@digital-platform/docusign/domain';
import { Signature } from '@digital-platform/sign-now/domain';
import { User } from '@digital-platform/users/domain';
import {
  SpecialWasteProfile,
  SpecialWasteProfileModel,
  WasteProfile,
  WasteProfileCustomerCard,
  WasteProfileStatus,
  WasteProfileTypeLabels,
} from '@digital-platform/waste-profiles/domain';
import { WasteProfilesFacadeService } from '@digital-platform/waste-profiles/services';

type WasteProfileCardTypes = WasteProfile | WasteProfileCustomerCard;

@Component({
  selector: 'digital-platform-waste-profile-card',
  templateUrl: './waste-profile-card.component.html',
  styleUrls: ['./waste-profile-card.component.scss'],
})
export class WasteProfileCardComponent implements OnChanges {
  @Input() wasteProfile: WasteProfileCardTypes;
  @Input() user: User;
  @Output() delete = new EventEmitter<WasteProfileCardTypes>();
  public showMakeRevisions = false;
  public showEdit = false;
  public showDelete = false;
  public showRecertify = false;
  public showDuplicate = false;
  public showChange = false;
  public showView = false;
  public showLandfill = false;
  public showExpiryDate = false;
  public showReviewed = false;
  public showStateWasteCode = false;
  public showExpectedApprovalDate = false;
  public showAnalyticalRequiredOnNextRecert = false;
  public editRecertification = false;
  public editChange = false;
  public showRecallSignature = false;
  public showSignProfile = false;
  public signNowUrl: string;
  public fullyExpired = false;
  public dateFormat = 'LLL.dd.yy';
  public statusLabel = '';
  public labels = WasteProfileTypeLabels;
  public customerStatus: WasteProfileStatus;

  @ViewChild('profileUnavailableDialog', { static: false })
  private profileUnavailableDialog: TemplateRef<WasteProfileCardComponent>;
  private removeDialogRef: MatDialogRef<WasteProfileCardComponent>;

  constructor(
    private dialog: MatDialog,
    private facade: WasteProfilesFacadeService
  ) {}

  public ngOnChanges(): void {
    if (this.wasteProfile) {
      const { status, isChange, isRecertification } = this.wasteProfile;
      this.showMakeRevisions = status === WasteProfileStatus.ACTION_REQUIRED;
      this.showEdit =
        status === WasteProfileStatus.NOT_SUBMITTED ||
        status === WasteProfileStatus.INCOMPLETE;
      this.showDelete = this._showDeleteProfileButton();
      this.showRecertify =
        status === WasteProfileStatus.EXPIRED ||
        status === WasteProfileStatus.EXPIRING;
      this.showDuplicate =
        (status === WasteProfileStatus.APPROVED || this.isFullyExpired()) &&
        SpecialWasteProfileModel.isSpecialWaste(this.wasteProfile);
      this.showChange = status === WasteProfileStatus.APPROVED;
      this.showView =
        status === WasteProfileStatus.SUBMITTED ||
        status === WasteProfileStatus.EXPIRING ||
        status === WasteProfileStatus.EXPIRED ||
        status === WasteProfileStatus.APPROVED;
      this.showReviewed = status === WasteProfileStatus.ACTION_REQUIRED;
      this.showAnalyticalRequiredOnNextRecert =
        this.wasteProfile.analyticalRequiredOnNextRecert &&
        (status === WasteProfileStatus.EXPIRING ||
          status === WasteProfileStatus.EXPIRED ||
          status === WasteProfileStatus.APPROVED);
      this.validateForLandfill();
      this.validateExpiryDate();
      this.validateApprovalDate();
      this.validateStateWasteCode();
      this.getCustomerStatusLabel(status);

      if (status === WasteProfileStatus.EXPIRING) {
        this.statusLabel = `${status} ${formatDistanceToNow(
          new Date(this.wasteProfile.expiryDate),
          { addSuffix: true }
        )}`;
      } else if (status === WasteProfileStatus.ACTION_REQUIRED) {
        this.statusLabel = WasteProfileStatus.REJECTED; // if customer status is Action required, then the status label should be rejected
      } else {
        this.statusLabel = status;
      }

      if (isRecertification) {
        this.editRecertification = true;
        this.editChange = false;
      } else if (isChange) {
        this.editChange = true;
        this.editRecertification = false;
      } else {
        this.editRecertification = false;
        this.editChange = false;
      }

      this.showRecallSignature = this._showRecallSignature(
        this.user,
        this.wasteProfile
      );
      this.showSignProfile = this._showSignProfile(
        this.user,
        this.wasteProfile
      );
    }
  }

  private _showDeleteProfileButton() {
    const { number, status } = this.wasteProfile;

    let shouldShowDelete = false;
    // right off the bat, if the profile has a number, then no deleting
    if (number && number.length > 0) {
      return shouldShowDelete;
    }

    // if the profile lacks a number, then we can delete if any of these conditions are true
    shouldShowDelete =
      status === WasteProfileStatus.NOT_SUBMITTED ||
      this.isFullyExpired() ||
      status === WasteProfileStatus.INCOMPLETE ||
      status === WasteProfileStatus.ACTION_REQUIRED;

    return shouldShowDelete;
  }

  private _showSignProfile(
    user: User,
    profile: WasteProfileCardTypes
  ): boolean {
    if (!user || !profile) {
      return false;
    }

    if (profile.status !== WasteProfileStatus.PENDING_SIGNATURE) {
      return false;
    }

    // if this is a 3pa situation, do not show sign button
    const { generator } = profile;
    if (
      generator.isCustomer3rdParty === true &&
      generator.is3rdPartyAuthorized === false
    ) {
      return false;
    }

    // TODO: Don't forget to account for change/recert

    const docusignData = this.getDocusignData(profile);

    if (!docusignData) {
      return false;
    }

    const pendingSignature = this.getPendingSignature(docusignData);

    if (!pendingSignature) {
      return false;
    }

    if (pendingSignature.email === user.email) {
      return true;
    }

    return false;
  }

  private _showRecallSignature(
    user: User,
    profile: WasteProfileCardTypes
  ): boolean {
    if (!user || !profile) {
      return false;
    }

    if (profile.status !== WasteProfileStatus.PENDING_SIGNATURE) {
      return false;
    }

    return !this._showSignProfile(user, profile);
  }

  private getDocusignData(profile: WasteProfileCardTypes): DocusignData {
    const { pendingChangeRequest, pendingRecertification } = profile;

    if (pendingChangeRequest) {
      return pendingChangeRequest.docusignData;
    } else if (pendingRecertification) {
      return pendingRecertification.docusignData;
    }

    return profile.docusignData;
  }

  private getPendingSignature(docusignData: DocusignData): Signature | null {
    if (!docusignData?.signatures || docusignData?.signatures?.length === 0) {
      return null;
    }

    return (
      [...docusignData.signatures]
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .find((signature) => !signature.isSigned) || null
    );
  }

  public isFullyExpired() {
    const { expiryDate, status } = this.wasteProfile;
    const twoYearAfterExpiry = addYears(new Date(expiryDate), 2);
    const today = new Date();
    return (
      isAfter(today, twoYearAfterExpiry) &&
      status === WasteProfileStatus.EXPIRED
    );
  }

  public duplicateTooltipText() {
    const duplicateText =
      '"Duplicate" creates a new copy of your profile. Contact your rep or coordinator to modify an existing profile.';
    return this.isFullyExpired() ? duplicateText : '';
  }

  public recertifyTooltipText() {
    const recertifyText =
      'Profiles may not be recertified two years or more after expiration. You must either create a new profile or duplicate this one.';
    return this.isFullyExpired() ? recertifyText : '';
  }

  public validateForLandfill() {
    this.showLandfill =
      this.wasteProfile.destination?.name &&
      (this.wasteProfile.status === WasteProfileStatus.EXPIRED ||
        this.wasteProfile.status === WasteProfileStatus.APPROVED ||
        this.wasteProfile.status === WasteProfileStatus.EXPIRING);
  }

  public validateStateWasteCode() {
    const { stateApprovalWasteCode } = this.wasteProfile as SpecialWasteProfile;
    this.showStateWasteCode =
      stateApprovalWasteCode &&
      (this.wasteProfile.status === WasteProfileStatus.EXPIRED ||
        this.wasteProfile.status === WasteProfileStatus.APPROVED ||
        this.wasteProfile.status === WasteProfileStatus.EXPIRING);
  }

  public validateExpiryDate() {
    this.showExpiryDate =
      (this.wasteProfile.expiryDate &&
        this.wasteProfile.status === WasteProfileStatus.EXPIRED) ||
      this.wasteProfile.status === WasteProfileStatus.APPROVED ||
      this.wasteProfile.status === WasteProfileStatus.EXPIRING;
  }
  public validateApprovalDate() {
    this.showExpectedApprovalDate =
      this.wasteProfile.status === WasteProfileStatus.SUBMITTED;
  }
  // if customer status is Action required, then the status label should be rejected
  public getCustomerStatusLabel(status: WasteProfileStatus) {
    if (status === WasteProfileStatus.ACTION_REQUIRED) {
      this.customerStatus = WasteProfileStatus.REJECTED;
    } else {
      this.customerStatus = status;
    }
  }
  public showProfileUnavailable() {
    this.dialog.open(this.profileUnavailableDialog, {
      width: '350px',
      height: '400px',
    });
  }

  public duplicate() {
    // TODO: Should probably change this up so that consuming features actually do the action?
    this.facade.duplicate(this.wasteProfile.id, true);
  }

  public openDialog(deleteDialog): void {
    this.removeDialogRef = this.dialog.open(deleteDialog, {
      width: '650px',
      height: '400px',
    });
  }

  deleteWasteProfile() {
    this.delete.emit(this.wasteProfile);
    if (this.removeDialogRef) {
      this.removeDialogRef.close();
    }
  }

  recallSignature() {
    if (this.removeDialogRef) {
      this.removeDialogRef.close();
    }
    this.facade.recallSignature(this.wasteProfile.id);
  }

  public viewProfileUrl() {
    const changeRequestDate = this.wasteProfile?.pendingChangeRequest
      ?.signNowDocument?.url
      ? new Date(this.wasteProfile.pendingChangeRequest?.signedDate)
      : null;
    const reCertificationDate = this.wasteProfile?.pendingRecertification
      ?.signNowDocument?.url
      ? new Date(this.wasteProfile.pendingRecertification?.signedDate)
      : null;

    let isChangeRequestAfter = null;

    if (changeRequestDate && reCertificationDate) {
      isChangeRequestAfter = isAfter(changeRequestDate, reCertificationDate);
    } else if (changeRequestDate) {
      isChangeRequestAfter = true;
    } else if (reCertificationDate) {
      isChangeRequestAfter = false;
    }

    if (isChangeRequestAfter === true) {
      this.signNowUrl =
        this.wasteProfile?.pendingChangeRequest?.signNowDocument?.url;
      return true;
    } else if (isChangeRequestAfter === false) {
      this.signNowUrl =
        this.wasteProfile?.pendingRecertification?.signNowDocument?.url;
      return true;
    } else if (this.wasteProfile.signNowDocument?.url) {
      this.signNowUrl = this.wasteProfile.signNowDocument?.url;
      return true;
    } else {
      return false;
    }
  }
}
